import { FieldValues } from "@modules/application/components/DynamicForm";
import {
  FormStep,
  NextStep,
  StepId,
} from "@modules/application/components/FormSteps/FormSteps";
import { useEffect, useMemo } from "react";
import { FormState } from "../context/ApplicationContext";

const isNeedToUpdateDefaultValue = (step: any) => {
  return step?.config?.fields?.some((field: any) => {
    return typeof field?.defaultValue === "function";
  });
};

const updateWithDefaultValue = async <T extends FieldValues>(
  step: FormStep<T>,
  value: any,
) => {
  if (!step.config.fields) {
    return;
  }

  await Promise.all(
    step.config.fields.map(async (field) => {
      if (typeof field?.defaultValue === "function" && !value[field.name]) {
        value[field.name] = await field.defaultValue(value);
      }
    }),
  );
};

const findLongestBranch = (steps: FormStep[], start?: FormStep): FormStep[] => {
  const stepMap = new Map<StepId, FormStep>();
  steps.forEach((step) => stepMap.set(step.id, step));

  let longestBranch: FormStep<any>[] = [];

  const dfs = (step: FormStep<any>, currentBranch: FormStep<any>[]) => {
    currentBranch.push(step);

    if (currentBranch.length > longestBranch.length) {
      longestBranch = [...currentBranch];
    }

    const children = step.possibleTransitions || [step.transition as StepId];
    for (const childId of children) {
      const child = stepMap.get(childId);
      if (child) {
        dfs(child, currentBranch);
      }
    }

    currentBranch.pop();
  };

  const startStep = start || steps[0];
  if (!startStep) return [];

  dfs(startStep, []);

  return longestBranch;
};

function flattenValues<T extends FieldValues>(
  values: FormState<T>["values"],
): T {
  return Object.keys(values).reduce(
    (prev, key) => ({ ...prev, ...values[key] }),
    {} as T,
  );
}

interface UseSteps<T extends FieldValues> {
  stepId: StepId;
  steps: FormStep<T>[];
  hasZeroStep: boolean;
  formState: FormState<T>;
  onSetForm: (formState: FormState<T>) => void;
  onComplete?: (step: FormStep<T>) => void;
  onGoBack: (stepId?: StepId) => void;
  onInvalidState: VoidFunction;
  onStepChange: (step: FormStep<T>) => void;
}

const useSteps = <T extends FieldValues>({
  stepId,
  steps,
  hasZeroStep,
  formState,
  onSetForm,
  onComplete,
  onGoBack,
  onInvalidState,
  onStepChange,
}: UseSteps<T>) => {
  // The step is missing when the page props arrive empty, which happens on a client-side
  // navigation to a rewritten path whose target was prerendered. Restarting the funnel beats
  // an error page.
  const currentStep = steps.find((s) => s.id === stepId) ?? steps[0];

  if (!currentStep) {
    throw new Error("Cannot render a form without steps");
  }

  const currentStepNumber = useMemo(() => {
    const stepIndex = formState.history.indexOf(currentStep.id);

    if (stepIndex >= 0) {
      return stepIndex;
    }

    return formState.history.length;
  }, [currentStep, formState.history]);

  const value: T = useMemo(
    () => flattenValues(formState.values),
    [formState.values],
  );

  useEffect(() => {
    if (
      Object.keys(formState.values).length === 0 &&
      currentStep !== steps[0]
    ) {
      onInvalidState();
    }
  }, [formState, currentStep, steps, onInvalidState]);

  const nextStep = async (value: any, stepId?: StepId) => {
    const transitionInput =
      typeof currentStep.transition === "function"
        ? value !== undefined
          ? flattenValues({
              ...formState.values,
              [currentStep.id]: value,
            })
          : flattenValues(formState.values)
        : undefined;

    const nextStepId =
      stepId ??
      (typeof currentStep.transition === "function"
        ? currentStep.transition(transitionInput as T)
        : currentStep.transition);

    if (typeof nextStepId === "undefined") {
      throw new Error(`Cannot find next step from ${currentStep.id}`);
    }

    const nextStep = steps.find((step) => step.id === nextStepId);

    if (nextStep) {
      if (isNeedToUpdateDefaultValue(nextStep)) {
        await updateWithDefaultValue(nextStep, value);
      }

      if (nextStep.id === currentStep.id) {
        throw new Error(
          `Potential loop detected, cannot transition from step ${nextStep.id} to ${currentStep.id}`,
        );
      }

      let values: FormState<T>["values"] = formState.values;

      if (value) {
        values = {
          ...formState.values,
          [currentStep.id]: value,
        };
      }

      const historyIndex = formState.history.indexOf(currentStep.id);
      let history: FormState<T>["history"];

      if (historyIndex >= 0) {
        history = formState.history.slice(0, historyIndex + 1);
      } else {
        history = [...formState.history, currentStep.id];
      }

      if (nextStep.navigateTo) {
        onComplete?.(nextStep);
        onSetForm({
          history,
          values,
        });
      } else {
        onSetForm({
          history,
          values,
        });
        onStepChange(nextStep);
      }
    } else {
      throw new Error(`Step with id ${nextStepId} does not exist.`);
    }
  };

  const previousStep = (stepId?: StepId) => {
    onGoBack(stepId);
  };

  const nextSteps = useMemo(() => {
    const completedSteps: NextStep[] = [];
    for (const stepId of formState.history) {
      if (stepId === currentStep.id) {
        break;
      }
      const step = steps.find((s) => s.id === stepId)!;
      completedSteps.push({
        label: step.label,
        id: step.id,
        config: step.config,
        completed: true,
        composite: step.composite,
      });
    }

    const result = completedSteps.concat(
      findLongestBranch(steps, currentStep)
        .filter((step) => !step.hidden)
        .map((step) => {
          return {
            label: step.label,
            id: step.id,
            config: step.config,
            completed: false,
            composite: step.composite,
          };
        }),
    );

    if (hasZeroStep) {
      return result.slice(1);
    }
    return result;
  }, [hasZeroStep, steps, formState, currentStep]);

  return {
    currentStep,
    value,
    numberOfSteps: nextSteps.length,
    currentStepNumber,
    handlePreviousStep: previousStep,
    handleNextStep: nextStep,
    nextSteps,
  };
};

export default useSteps;
